import { api } from './client';
import type { ImportResult } from './items';

export interface WarehouseStockRow {
  itemId: number;
  itemCode: string;
  itemName: string;
  imageUrl?: string | null;
  barcode: string | null;
  unit: string;
  category: string | null;
  mrp: number;
  purchaseRate: number;
  openingQty: number;
  receivedQty: number;
  issuedQty: number;
  /** Purchases received on GRNs. */
  grnInQty: number;
  /** Audit / manual corrections, returns and transfers in. */
  adjustedInQty: number;
  /** Outward GRNs, sales, STN / transfers out, department issues. */
  dispatchedQty: number;
  /** Audit / manual write-offs. */
  adjustedOutQty: number;
  currentQty: number;
  reservedQty: number;
  availableQty: number;
  reorderLevel: number;
  stockStatus: 'In Stock' | 'Low Stock' | 'Out of Stock' | 'Not Stocked Yet';
  stockValue: number;
  /** Pcs at the filtered location — null when no location filter is set. */
  locationQty: number | null;
  lastUpdatedAt: string;
}

export interface WarehouseStockKpis {
  totalItems: number;
  /** Pcs on hand across all items (sum of current qty). */
  totalPcs: number;
  outOfStock: number;
  lowStock: number;
  /** Items with a stock row that have never had any stock. */
  notStocked: number;
  totalValue: number;
}

export interface WarehouseLedgerRow {
  id: string;
  txnDate: string;
  txnType: string;
  refDoc: string | null;
  itemId: number;
  itemCode: string;
  itemName: string;
  barcode?: string | null;
  imageUrl?: string | null;
  unit: string;
  qtyIn: number;
  qtyOut: number;
  balance: number;
  /** GRN line: the GRN's price; otherwise the item's purchase rate. */
  rate: number;
  /** (qty in + qty out) × rate. */
  value: number;
  remarks: string | null;
  createdAt: string;
}

export interface WarehouseStockList {
  data: WarehouseStockRow[];
  total: number;
  /** KPI cards for the filtered set (every filter except the bucket). */
  kpis?: WarehouseStockKpis;
  /** Floor area of the pcs on hand across the whole filtered set. */
  area?: { sqft: number; sqm: number; pcs: number; unsizedItems: number };
  page: number;
  pageSize: number;
}

export async function listWarehouseStock(filter: {
  search?: string;
  bucket?: 'all' | 'low' | 'zero' | 'ok' | 'unstocked';
  /** Item collection, or NO_COLLECTION for items without one. */
  collection?: string;
  category?: string;
  /** Only items with units at this location. */
  locationId?: number;
  page?: number;
  pageSize?: number;
} = {}): Promise<WarehouseStockList> {
  const r = await api.get<WarehouseStockList>('/warehouse-stock', { params: filter });
  return r.data;
}

/** All low/out-of-stock rows for the bulk "Generate MRN" action. */
export async function fetchWarehouseShortages(): Promise<WarehouseStockRow[]> {
  const r = await api.get<WarehouseStockRow[]>('/warehouse-stock/shortages');
  return r.data;
}

/** Path for the "Export" download — the WHOLE filtered stock, no row limit. */
// ── Stock ageing ─────────────────────────────────────────────────────────────
export interface StockAgeingRow {
  itemId: number;
  code: string;
  name: string;
  barcode: string | null;
  category: string | null;
  collection: string | null;
  unit: string;
  imageUrl: string | null;
  rate: number;
  pcs: number;
  value: number;
  /** Pieces aged 0–30, 31–60, 61–90 and 90+ days. */
  buckets: [number, number, number, number];
  /** Value of each bucket at purchase rate. */
  values: [number, number, number, number];
  oldestDays: number;
  oldestReceived: string;
}

export interface StockAgeing {
  asOf: string;
  totals: { pcs: number; value: number; items: number; buckets: { label: string; pcs: number; value: number; items: number }[] };
  rows: StockAgeingRow[];
  /** Earliest received date of opening-stock units (their go-live date), if any. */
  openingSince: string | null;
  openingPcs: number;
}

export type StockAgeingFilter = { search?: string; collection?: string; category?: string; locationId?: number };

export async function getStockAgeing(filter: StockAgeingFilter = {}): Promise<StockAgeing> {
  const r = await api.get<StockAgeing>('/warehouse-stock/ageing', { params: filter });
  return r.data;
}

export function stockAgeingExportPath(filter: StockAgeingFilter = {}): string {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])),
  ).toString();
  return `/warehouse-stock/ageing/export.xlsx${q ? `?${q}` : ''}`;
}

/** Download path for the serial tracker's Excel export (same filters as the list). */
export function warehouseSerialsExportPath(filter: { search?: string; status?: string; locationId?: number } = {}): string {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])),
  ).toString();
  return `/warehouse-stock/serials/export.xlsx${q ? `?${q}` : ''}`;
}

/** Collection filter value for items that have no collection set. */
export const NO_COLLECTION = '__none__';

export async function listStockCollections(): Promise<{ collections: { name: string; items: number }[]; withoutCollection: number }> {
  const r = await api.get('/warehouse-stock/collections');
  return r.data;
}

/** Categories in warehouse stock, with item counts — for the Category filter. */
export async function listStockCategories(): Promise<{ name: string; items: number }[]> {
  const r = await api.get('/warehouse-stock/categories');
  return r.data;
}

export function warehouseStockExportPath(
  filter: { search?: string; bucket?: string; collection?: string; category?: string; locationId?: number | '' } = {},
): string {
  const search = new URLSearchParams(
    Object.fromEntries(
      Object.entries(filter).filter(([, v]) => v != null && v !== '' && v !== 'all').map(([k, v]) => [k, String(v)]),
    ) as Record<string, string>,
  ).toString();
  return `/warehouse-stock/export.xlsx${search ? `?${search}` : ''}`;
}

export async function importWarehouseStock(file: File): Promise<ImportResult> {
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post<ImportResult>('/warehouse-stock/import', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000,
  });
  return r.data;
}

export async function listWarehouseLedger(filter: {
  itemId?: number;
  txnType?: string;
  from?: string;
  to?: string;
  search?: string;
} = {}): Promise<WarehouseLedgerRow[]> {
  const r = await api.get<WarehouseLedgerRow[]>('/warehouse-stock/ledger', {
    params: filter,
  });
  return r.data;
}

export interface AdjustResult {
  itemId: number;
  currentQty: string;
  direction: 'in' | 'out';
  /** Serial labels minted for an addition (empty for a deduction). */
  serials: string[];
  locationCode: string | null;
  item: { code: string; name: string; category: string | null; unit: string; barcode: string | null };
}

export async function adjustWarehouseStock(payload: {
  itemId: number;
  direction: 'in' | 'out';
  qty: number;
  reason: string;
  refDoc?: string;
  barcode?: string;
  locationCode?: string;
}): Promise<AdjustResult> {
  const r = await api.post<AdjustResult>('/warehouse-stock/adjust', payload);
  return r.data;
}

export interface PrepareAdjustResult {
  itemId: number;
  serials: string[];
  /** Pieces on each label, parallel to serials. */
  quantities?: number[];
  item: { code: string; name: string; category: string | null; unit: string; barcode: string | null };
}

/** Scan-confirmed addition — step 1: mint Pending serial labels (no stock change). */
export async function prepareAdjustmentLabels(payload: {
  itemId: number;
  /** Number of labels. */
  qty: number;
  /** Pieces per label, one per label. */
  quantities?: number[];
  barcode?: string;
}): Promise<PrepareAdjustResult> {
  const r = await api.post<PrepareAdjustResult>('/warehouse-stock/adjust/prepare', payload);
  return r.data;
}

/** Scan-confirmed addition — step 2: commit scanned labels → stock + ledger + tracker. */
export async function commitAdjustmentAddition(payload: {
  itemId: number;
  serialNos: string[];
  reason: string;
  refDoc?: string;
  locationCode?: string;
}): Promise<AdjustResult> {
  const r = await api.post<AdjustResult>('/warehouse-stock/adjust/commit', payload);
  return r.data;
}

/** Deduction — remove the user-selected in-warehouse serials from stock. */
export async function deductAdjustmentStock(payload: {
  itemId: number;
  serialNos: string[];
  /** Pieces to take off each serial (parallel to serialNos); omitted = whole serial. */
  quantities?: number[];
  reason: string;
  refDoc?: string;
  barcode?: string;
}): Promise<AdjustResult> {
  const r = await api.post<AdjustResult>('/warehouse-stock/adjust/deduct', payload);
  return r.data;
}

// ---- Physical stock audit ----
export interface AuditSerial {
  serialNo: string;
  status: string;
  itemId: number;
  /** Pieces this serial represents (1 for a per-unit label, N for a box label). */
  qty: number;
  item: { code: string; name: string; unit: string };
  location: { code: string; name: string } | null;
}

export interface AuditReconcileResult {
  scope: 'location' | 'item' | 'all';
  scannedCount: number;
  inScopeCount: number;
  matchedCount: number;
  /** Piece (unit) totals — a serial may be a box of N, so these reflect real stock. */
  inScopeUnits: number;
  matchedUnits: number;
  missingUnits: number;
  extraFoundUnits: number;
  balanced: boolean;
  /** In-scope InWarehouse serials that were NOT scanned → candidates to retire. */
  missing: AuditSerial[];
  /** Scanned serials that exist but are not InWarehouse → candidates to add back. */
  extraFound: AuditSerial[];
  /** Scanned serials the system has never seen — cannot auto-add. */
  extraUnknown: string[];
  /** Scanned serials that are on hand, but outside the audited scope (a by-location audit can move them in). */
  onHandElsewhere: AuditSerial[];
}

export async function auditReconcile(payload: {
  scope: 'location' | 'item' | 'all';
  locationId?: number;
  itemIds?: number[];
  scannedSerials: string[];
}): Promise<AuditReconcileResult> {
  const r = await api.post<AuditReconcileResult>('/warehouse-stock/audit/reconcile', payload);
  return r.data;
}

export async function auditApplyCorrections(payload: {
  deductSerials: string[];
  /** Pieces to retire from each (parallel to deductSerials); omitted = whole serial. */
  deductQuantities?: number[];
  addSerials: string[];
  /** Pieces each found serial holds now (parallel to addSerials). */
  addQuantities?: number[];
  reason: string;
  refDoc?: string;
}): Promise<{ deducted: number; added: number; deductedUnits?: number; addedUnits?: number }> {
  const r = await api.post<{ deducted: number; added: number; deductedUnits?: number; addedUnits?: number }>('/warehouse-stock/audit/apply', payload);
  return r.data;
}

/** Audit — move units scanned in the audited location (but recorded elsewhere) into it. */
export async function auditMoveSerials(payload: {
  locationId: number;
  serialNos: string[];
}): Promise<{ moved: number; alreadyThere: number; location: { code: string; name: string }; from: Record<string, number> }> {
  const r = await api.post('/warehouse-stock/audit/move', payload);
  return r.data;
}

/** Audit — add brand-new (unrecognised) serials against a chosen item. */
export async function auditAddNewSerials(payload: {
  itemId: number;
  serialNos: string[];
  /** Pieces each new barcode holds (parallel to serialNos); omitted = 1 each. */
  quantities?: number[];
  reason: string;
  refDoc?: string;
  locationCode?: string;
}): Promise<{ added: number; itemId: number; locationCode: string | null; item: { code: string; name: string } }> {
  const r = await api.post('/warehouse-stock/audit/add-new', payload);
  return r.data;
}

/** Cancel a prepared batch — delete its still-Pending serials. */
export async function discardAdjustmentLabels(serialNos: string[]): Promise<{ discarded: number }> {
  const r = await api.post<{ discarded: number }>('/warehouse-stock/adjust/discard', { serialNos });
  return r.data;
}

export async function setReorderLevel(payload: {
  itemId: number;
  reorderLevel: number;
}): Promise<{ itemId: number; reorderLevel: number }> {
  const r = await api.post('/warehouse-stock/set-reorder', payload);
  return r.data;
}

export interface WarehouseSerial {
  id: number;
  serialNo: string;
  itemId: number;
  /** Pieces this barcode holds (Decimal, arrives as a string). */
  qty: string | number;
  grnId: number | null;
  grnItemId: number | null;
  source: 'GRN' | 'Opening' | 'Adjustment';
  status: 'InWarehouse' | 'Packed' | 'AtDepartment' | 'Dispatched' | 'Pending';
  /** Department the unit is out at (status AtDepartment). */
  department?: string | null;
  receivedAt: string;
  dispatchedAt: string | null;
  packingId: number | null;
  item: { id: number; code: string; name: string; unit: string; category: string | null; imageUrl?: string | null; barcode?: string | null };
  grn: { id: number; grnNo: string; grnDate: string } | null;
  location: { id: number; code: string; name: string; level: string } | null;
}

export interface WarehouseSerialList {
  rows: WarehouseSerial[];
  total: number;
  byStatus: { InWarehouse: number; Packed: number; AtDepartment: number; Dispatched: number };
  cap: number;
}

export async function listWarehouseSerials(filter: {
  search?: string;
  status?: 'InWarehouse' | 'Packed' | 'AtDepartment' | 'Dispatched';
  itemId?: number;
  locationId?: number;
} = {}): Promise<WarehouseSerialList> {
  const r = await api.get<WarehouseSerialList>('/warehouse-stock/serials', { params: filter });
  return r.data;
}

export interface LocationOccupancyRow {
  locationId: number;
  code: string;
  name: string;
  level: string;
  active: boolean;
  parentPath: string | null;
  capacity: number;
  occupied: number;
  free: number;
  utilizationPct: number | null;
}

export async function listLocationOccupancy(filter: {
  search?: string;
  nonEmptyOnly?: boolean;
} = {}): Promise<LocationOccupancyRow[]> {
  const r = await api.get<LocationOccupancyRow[]>('/warehouse-stock/location-occupancy', {
    params: {
      ...(filter.search ? { search: filter.search } : {}),
      ...(filter.nonEmptyOnly ? { nonEmptyOnly: 'true' } : {}),
    },
  });
  return r.data;
}

export interface ItemReservation {
  doId: number;
  doNo: string;
  doDate: string;
  /** Shopify order number for online orders, otherwise the enquiry number. */
  orderRef: string | null;
  online: boolean;
  customer: string | null;
  qty: number;
  stage: string;
}

/** Open orders (Dispatch Orders) holding an item's reserved stock. */
export async function getItemReservations(itemId: number): Promise<{
  itemCode: string | null;
  itemName: string | null;
  reservedQty: number;
  totalOnOrders: number;
  rows: ItemReservation[];
}> {
  return (await api.get(`/warehouse-stock/reservations/${itemId}`)).data;
}
